/**
 * Field-level validation hook.
 *
 * Tracks whether the input has been "touched" so errors don't appear
 * on first render of an empty form (no one wants every required
 * field flagged before the user has typed anything).
 *
 * The trigger is configurable via `validateOn`:
 *  - "change" — re-evaluates on every value change (after first touch)
 *  - "blur"   — only updates errors when the field loses focus
 *  - "submit" — never auto-shows; surface only when submitForm() is
 *               called via the §1.6 actions catalog (future hook-up)
 *
 * The hook returns errors plus event helpers the input wires to its
 * onBlur / onChange handlers — keeps the field component declarative.
 */

import { useCallback, useEffect, useMemo, useState } from "react"

import { evaluateChecks, type ValidateTrigger, type ValidationCheck } from "../utils/validation"

// Track which fields warned about the unwired submit-trigger so a
// large form doesn't print N warnings; key by `checks` reference
// (each call site gets one warning at most).
const warnedSubmitFields = new WeakSet<ValidationCheck[]>()

export interface FieldValidation {
  /** Visible errors (empty until touched + at least one check failed). */
  errors: string[]
  /** Wire to <Input onBlur />; surfaces errors on first blur. */
  onBlur: () => void
  /** Wire to <Input onChange />; only relevant when validateOn="change". */
  onChange: (value: unknown) => void
  /** True iff the user has interacted with the field yet. */
  touched: boolean
}

export function useFieldValidation(
  value: unknown,
  config: { checks?: ValidationCheck[]; validateOn?: ValidateTrigger } | undefined
): FieldValidation {
  const validateOn: ValidateTrigger = config?.validateOn ?? "blur"
  const checks = config?.checks
  const [touched, setTouched] = useState(false)

  // Until §1.6's submitForm action handler is wired, validateOn:"submit"
  // is a stub — errors will never surface. Warn once per field so the
  // spec author notices their checks aren't actually running. Logged
  // only when there ARE checks (an empty checks array isn't useful to
  // flag).
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return
    if (validateOn !== "submit") return
    if (!checks || checks.length === 0) return
    if (warnedSubmitFields.has(checks)) return
    warnedSubmitFields.add(checks)
    console.warn(
      "[dash] validateOn:'submit' is declared but no submitForm action is wired yet — these checks (%o) will never surface as errors. Use 'change' or 'blur' until the submit action lands.",
      checks
    )
  }, [validateOn, checks])

  const errors = useMemo(() => {
    if (!touched) return []
    if (validateOn === "submit") return [] // surfaced externally
    return evaluateChecks(value, checks)
  }, [touched, validateOn, value, checks])

  const onBlur = useCallback(() => {
    if (!touched) setTouched(true)
  }, [touched])

  const onChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (_value: unknown) => {
      if (validateOn === "change" && !touched) setTouched(true)
    },
    [validateOn, touched]
  )

  return { errors, onBlur, onChange, touched }
}
